import {
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Drawer,
  FormInstance,
  Row,
  Spin,
  Table,
  Tooltip,
  Typography,
  message,
  Card as AntdCard,
  Tag,
} from "antd";

import { AxiosResponse } from "axios";
import * as Sentry from "@sentry/browser";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import {
  ActiveDrawerContent,
  APIError,
  Attachment,
  Changelog,
  CustomerLien,
  CustomerTransaction,
  Note,
  NotificationRecord,
  PaymentAccount,
  RedemptionRequestAttachment,
  RequestReason,
  SelectOptionsInterface,
  TransitionToNextState,
  User,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
  generateStateButtonText,
  generateDisplayErrorMessage,
  getUniqueArrayObjectsByKey,
  handleValidationErrors,
  request,
  responseHandler,
  spaceCamel,
  getStateColor,
} from "../helpers/utilities";

import moment from "moment";
import ModalCreator from "../components/ModalCreator";
import {
  ASSIGN_ATTACHMENT_TO_CUSTOMER_TRANSACTION,
  ASSIGN_NOTE_TO_CUSTOMER_TRANSACTION,
  GET_ONLINE_TRANSACTION,
  GET_TRANSACTION_CHANGE_LOGS,
  GET_TRANSACTION_NOTIFIER_RECORDS_QUERY,
  MOVE_CUSTOMER_TRANSACTION_TO_STATE,
  UNASSIGN_ATTACHMENT_FROM_CUSTOMER_TRANSACTION,
  UPDATE_CUSTOMER_TRANSACTION_ALLOCATION_AMOUNT,
} from "../gql/online-transactions";
import {
  RedemptionAllocationBreakdown,
  RequestReasonUI,
} from "./ViewRedemptionRequest";
import NotesSection from "../components/NotesSection";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { CREATE_NOTE } from "../gql/notes";
import { GET_REDEMPTION_REQUEST_REASONS } from "../gql/redemption-request";
import { tabPaneStyle } from "../helpers/constants";
import { PaymentAccountCard } from "../components/PaymentsAccountsView";
import AttachmentsSection from "../components/AttachmentsSection";
import { CREATE_ATTACHMENT } from "../gql/general";
import {
  notificationColumns,
  NotificationRecordTableFactory,
} from "./NotificationRecords";
import RequestChangeLogs from "../components/RequestChangeLogs";

const { Panel } = Collapse;
const { Text } = Typography;

interface State {
  isLoading: boolean;
  id: string | number;
  customerTransaction?: CustomerTransaction;
  showDrawer: boolean;
  transactionChangeLogs: Changelog[];
  notes: Note[];
  date: string;
  currentCustomerTransactionToProcess: number;
  redemptionRequestReasons: RequestReason[];
  attachments: Attachment[];
  attachmentIds: string[];
  redemptionRequestAttachments: RedemptionRequestAttachment[];
  showReasonModal: boolean;
  isReasonLoading: boolean;
  selectedTransitionState?: string;
  isProcessingRedemption: boolean;
  commentRequired: boolean;
  availableTransitionStates?: TransitionToNextState[];
  activeDrawerContent: ActiveDrawerContent;
  showCancelModal: boolean;
  notifications?: NotificationRecord[];
  showConfirmationModal: boolean;
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { customerTransactions?: CustomerTransaction[] }
  > {
  user: User;
}

export default class ViewCustomerTransaction extends React.Component<
  Props,
  State
> {
  noteReference: RefObject<FormInstance>;
  attachmentReference: RefObject<FormInstance>;
  reasonReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      customerTransaction: undefined,
      showDrawer: false,
      transactionChangeLogs: [],
      notes: [],
      redemptionRequestReasons: [],
      date: "",
      currentCustomerTransactionToProcess: 0,
      attachments: [],
      attachmentIds: [],
      redemptionRequestAttachments: [],
      showReasonModal: false,
      isReasonLoading: false,
      isProcessingRedemption: false,
      commentRequired: false,
      activeDrawerContent: "",
      showCancelModal: false,
      showConfirmationModal: false,
    };
    this.attachmentReference = React.createRef<FormInstance>();
    this.noteReference = React.createRef<FormInstance>();
    this.reasonReference = React.createRef<FormInstance>();
  }

  getCustomerTransaction = (): void => {
    const { id } = this.state;
    interface Response {
      data: {
        customerTransaction: CustomerTransaction;
      };
    }
    request({
      query: GET_ONLINE_TRANSACTION,
      variables: { id },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        customerTransaction: data.data.customerTransaction,
        isLoading: false,
        availableTransitionStates:
          data.data.customerTransaction?.canTransitionToNextStates,
        notes: data.data.customerTransaction?.customerTransactionNotes ?? [],
        attachments:
          data.data.customerTransaction?.redemptionRequestAttachments?.map(
            (redemptionRequestAttachment) =>
              redemptionRequestAttachment.attachment,
          ) ?? [],
        redemptionRequestAttachments:
          data.data.customerTransaction.redemptionRequestAttachments ?? [],
      });
      this.getTransactionChangeLogs();
    });
  };

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        isLoading: true,
        currentCustomerTransactionToProcess:
          this.props.location?.state?.customerTransactions?.findIndex(
            (item) =>
              item.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getCustomerTransaction();
      },
    );
  }

  getPayoutAccount(payoutAccounts: PaymentAccount[]): JSX.Element {
    const uniquePayoutAccounts = getUniqueArrayObjectsByKey(
      payoutAccounts,
      "accountNumber",
    );
    if (uniquePayoutAccounts.length < 0) {
      return <></>;
    }

    return (
      <div>
        {uniquePayoutAccounts.map((account) => (
          <div key={account.id}>
            {`${account.bankBranch?.name} - ${account.accountNumber} - ${account.bankBranch?.sortCode}`}
          </div>
        ))}
      </div>
    );
  }

  getTransactionChangeLogs = (): void => {
    const { customerTransaction } = this.state;
    interface Response {
      customerTransactionChangeLogs: Changelog[];
    }

    this.setState({ isLoading: true });
    request({
      query: GET_TRANSACTION_CHANGE_LOGS,
      variables: { customerTransactionId: customerTransaction?.id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        this.setState({
          transactionChangeLogs: data?.data?.customerTransactionChangeLogs,
        });
      })
      .catch((error) => {
        message.error("Failed to get transaction change logs");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  generateButtonColor = (state: string): string => {
    if (["reversed", "cancelled"].includes(state)) return "error-btn";
    if (["paid", "approved", "redemption_approved"].includes(state))
      return "success-btn";
    return "warning-btn";
  };

  saveStateReason = async (): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    const { selectedTransitionState } = this.state;

    let data;

    if (!this.reasonReference.current) return;

    const fieldsToBeValidated = [
      "redemptionRequestReasonId",
      "comment",
      "amount",
      "date",
    ];

    try {
      data = await this.reasonReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    interface Response {
      customerTransaction: CustomerTransaction;
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true });
    const now = moment();
    const variables = {
      customerTransactionId: this.state.id,
      ...data,
      // the picker only captures a day, so stamp the current time onto it -
      // the API takes a datetime and a bare date would land at midnight
      date: data.date
        ? moment(data.date).set({
            hour: now.hour(),
            minute: now.minute(),
            second: now.second(),
            millisecond: now.millisecond(),
          })
        : data.date,
      state: selectedTransitionState,
    };
    try {
      const response: AxiosResponse = await request({
        query: MOVE_CUSTOMER_TRANSACTION_TO_STATE,
        variables,
      });

      const { err } = responseHandler<Response>(
        response,
        "moveCustomerTransactionToState",
      );

      if (err) {
        message.error(formatSnakeCase(generateDisplayErrorMessage(err)));
      } else {
        message.success("Transaction state updated successfully");
        this.getCustomerTransaction();
      }
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isReasonLoading: false, showReasonModal: false });
    }
  };

  updateCustomerTransactionAllocationAmount = async (): Promise<void> => {
    interface Response {
      customerTransaction: CustomerTransaction;
      errors: APIError[];
    }

    this.setState({ isLoading: true });
    const variables = {
      customerTransactionId: this.state.id,
    };

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_TRANSACTION_ALLOCATION_AMOUNT,
        variables,
      });

      const { err } = responseHandler<Response>(
        response,
        "updateCustomerTransactionAllocationAmounts",
      );

      if (err) {
        message.error(formatSnakeCase(generateDisplayErrorMessage(err)));
      } else {
        message.success("Transaction allocation amounts updated successfully");
        this.getCustomerTransaction();
      }
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, showConfirmationModal: false });
    }
  };

  toggleConfirmationModal = () => {
    this.setState((prevState) => ({
      showConfirmationModal: !prevState.showConfirmationModal,
    }));
  };

  requestForReason = async (state: string): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    interface Response {
      redemptionRequestReasons: RequestReason[];
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true });

    try {
      request({
        query: GET_REDEMPTION_REQUEST_REASONS,
        variables: { state },
      })
        .then(({ data }: AxiosResponse<{ data: Response }>) => {
          this.setState({
            redemptionRequestReasons: data.data.redemptionRequestReasons,
            showReasonModal: true,
            selectedTransitionState: state,
          });
        })
        .finally(() => {
          this.setState({ isReasonLoading: false });
        });
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({ isReasonLoading: false });
    }
  };

  getDrawerContent = (): JSX.Element => {
    const { transactionChangeLogs, activeDrawerContent, isLoading } =
      this.state;

    switch (activeDrawerContent) {
      case "audits":
        return (
          <RequestChangeLogs
            data={transactionChangeLogs}
            isLoading={isLoading}
          />
        );
      case "notifierRecords":
        const records = this.state.customerTransaction
          ?.notifierRecords as NotificationRecord[];

        return (
          <Fragment>
            {NotificationRecordTableFactory(records, notificationColumns)}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Online Transaction Change Logs";
      case "notifierRecords":
        return "Notification Records";
      default:
        return "Entity";
    }
  };

  toggleModal = (): void => {
    this.setState({
      showCancelModal: !this.state.showCancelModal,
    });
  };

  getTransactionNotifierRecords = (): void => {
    const { id } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_TRANSACTION_NOTIFIER_RECORDS_QUERY,
      variables: { id },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            customerTransaction: CustomerTransaction;
          };
        }>): void => {
          this.setState({
            customerTransaction: {
              ...this.state.customerTransaction,
              ...data.data.customerTransaction,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  executeDrawerQuery(currentDrawer: ActiveDrawerContent): void {
    switch (currentDrawer) {
      case "notifierRecords":
        this.getTransactionNotifierRecords();
        break;
      case "audits":
        this.getTransactionChangeLogs();
        break;
      default:
        break;
    }
    return;
  }

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");
      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState(
        (state) => ({
          attachmentIds: [...state.attachmentIds, mutation.attachment.id],
          attachments: [...state.attachments, mutation.attachment],
        }),
        () =>
          this.assignAttachmentToCustomerTransaction(
            this.state.attachmentIds,
            this.state.id,
          ),
      );

      this.attachmentReference.current &&
        this.attachmentReference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  unassignAttachmentFromCustomerTransaction = async (
    id: string,
  ): Promise<void> => {
    const { redemptionRequestAttachments } = this.state;

    const redemptionRequestAttachmentToDelete:
      | RedemptionRequestAttachment
      | undefined = redemptionRequestAttachments.find(
      (cra: RedemptionRequestAttachment) => cra.attachment.id === id,
    );

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_ATTACHMENT_FROM_CUSTOMER_TRANSACTION,
        variables: { id: redemptionRequestAttachmentToDelete?.id },
      });
      const { mutation } = responseHandler<Response>(
        response,
        "unassignAttachmentFromCustomerTransaction",
      );

      if (!mutation.result) {
        message.error("Failed to delete customer transaction attachment");
      } else {
        const attachments = this.state.attachments.filter(
          (attachment) => attachment.id !== id,
        );
        this.setState({ attachments });
        message.success("Deleted Attachment Successfully");
      }
    } catch (error) {
      message.error(`An error occurred, please refresh and try again later - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createNote = async (): Promise<void> => {
    const { user } = this.props;

    interface Response {
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.noteReference.current) return;

    let data;

    try {
      data = await this.noteReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(response, "createNote");
      this.assignNoteToCustomerTransaction(mutation.note.id);
      (await this.noteReference.current) &&
        this.noteReference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({
        isLoading: false,
      });
    }
  };

  assignNoteToCustomerTransaction = (noteId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignNoteToCustomerTransaction: {
          customerTransactionNote: {
            note: Note;
          };
        };
      };
    }

    return request({
      query: ASSIGN_NOTE_TO_CUSTOMER_TRANSACTION,
      variables: { noteId: noteId, customerTransactionId: this.state.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        notes: [
          ...this.state.notes,
          data.assignNoteToCustomerTransaction.customerTransactionNote.note,
        ],
      });
    });
  };

  assignAttachmentToCustomerTransaction = async (
    attachmentIds: string[],
    customerTransactionId: string | number,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToCustomerTransaction: {
          customerTransaction: {
            redemptionRequestAttachments: RedemptionRequestAttachment[];
          };
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_CUSTOMER_TRANSACTION,
      variables: {
        attachmentIds,
        customerTransactionId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        attachmentIds: [],
        redemptionRequestAttachments:
          data.data.assignAttachmentsToCustomerTransaction.customerTransaction
            .redemptionRequestAttachments,
      });
    });
  };

  toggleShowReasonModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showReasonModal: !previousState.showReasonModal,
    }));
  };

  completionDateChangeHandler = async (): Promise<void> => {
    this.setState({
      date: this.reasonReference.current?.getFieldValue("date")._d,
    });
  };

  handleReasonSelect = (reason: string) => {
    this.setState({ commentRequired: reason === "6" }); // 6 = other reasons
  };

  handleNoteUpdate = (updatedNotes: Note[]) => {
    this.setState({ notes: updatedNotes });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      customerTransaction,
      currentCustomerTransactionToProcess,
      redemptionRequestReasons,
      attachments,
      notes,
      showDrawer,
      showReasonModal,
      commentRequired,
      availableTransitionStates,
      selectedTransitionState,
      showConfirmationModal,
      isReasonLoading,
    } = this.state;

    const allCustomerLiens =
      customerTransaction?.customerSubscription.customerLiens;

    const reasons: SelectOptionsInterface[] = redemptionRequestReasons?.map(
      (reason) => {
        return { id: reason.id, name: reason.reason.reasonDescription };
      },
    );

    const noReason: boolean = reasons?.length <= 1;

    const reason = customerTransaction?.customerTransactionsReasons
      ?.filter((reason) => {
        return reason.customerTransactionStatus === customerTransaction.status;
      })
      ?.slice(-1)[0];

    const moveToNextLink = `/online-transactions/${
      this.props.location?.state?.customerTransactions?.[
        currentCustomerTransactionToProcess + 1
      ]?.id
    }`;
    const moveToPreviousLink = `/online-transactions/${
      this.props.location?.state?.customerTransactions?.[
        currentCustomerTransactionToProcess - 1
      ]?.id
    }`;

    const nextPreviousUI = (
      <>
        <br />
        <Row
          gutter={10}
          style={{
            float: "right",
          }}
        >
          <Col>
            {currentCustomerTransactionToProcess > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    customerTransactions:
                      this.props?.location?.state?.customerTransactions ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title={"Previous"}>
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.customerTransactions &&
              currentCustomerTransactionToProcess <
                this.props?.location?.state?.customerTransactions?.length -
                  1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      customerTransactions:
                        this.props?.location?.state?.customerTransactions ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title={"Next"}>
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.customerTransactions &&
              `${currentCustomerTransactionToProcess + 1} / ${
                this.props?.location?.state?.customerTransactions?.length
              }`}
          </Col>
        </Row>
      </>
    );

    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Row>
                <Col span={24}>
                  <Descriptions
                    title={<div>Details</div>}
                    extra={
                      <div>
                        <Button
                          className="base-button"
                          onClick={this.toggleDrawer("audits")}
                        >
                          Request History
                        </Button>
                        <Button
                          style={{
                            marginLeft: "5px",
                          }}
                          className="base-button"
                          onClick={this.toggleDrawer("notifierRecords")}
                        >
                          Notifications
                        </Button>
                        <Button
                          style={{
                            marginLeft: "5px",
                            marginBottom: "2px",
                          }}
                          className="base-button"
                          onClick={this.toggleConfirmationModal}
                        >
                          Update Allocation Amount
                        </Button>
                        {nextPreviousUI}
                      </div>
                    }
                  />
                </Col>
              </Row>
              <AntdCard title="Customer">
                <Descriptions>
                  <Descriptions.Item label={<b>Customer</b>} key="customer">
                    {customerTransaction?.customer ? (
                      <NavLink
                        to={`/customers/${customerTransaction.customer.id}`}
                      >
                        {`${customerTransaction.customer.petraId} | ${customerTransaction.customer.fullname}`}
                      </NavLink>
                    ) : (
                      "N/A"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Date Of Birth</b>} key="dateOfBirth">
                    {customerTransaction?.customer?.dateOfBirth || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>ID Type</b>} key="idType">
                    {customerTransaction?.idType?.name || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>ID Number</b>} key="idNumber">
                    {customerTransaction?.idNumber || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Client</b>} key="company">
                    {customerTransaction?.company ? (
                      <NavLink
                        to={`/companies/${customerTransaction.company.id}`}
                      >
                        {`${customerTransaction.company.petraId} | ${customerTransaction.company.name}`}
                      </NavLink>
                    ) : (
                      "N/A"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={<b>Employment Date</b>}
                    key="employmentDate"
                  >
                    {customerTransaction?.employmentDate || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Contact Information</b>}
                    key="contactInformation"
                  >
                    {customerTransaction?.contactInformation || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Exit Date</b>} key="exitDate">
                    {customerTransaction?.exitDate || "N/A"}
                  </Descriptions.Item>
                  {customerTransaction?.reference && (
                    <Descriptions.Item label={<b>Reference No.</b>}>
                      {customerTransaction?.reference}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </AntdCard>

              <AntdCard
                title={
                  <span>
                    Online Transaction{" "}
                    {customerTransaction?.status ? (
                      <Tag
                        color={getStateColor(customerTransaction.status)}
                        style={{
                          textTransform: "capitalize",
                          marginLeft: "3px",
                        }}
                      >
                        {formatSnakeCase(customerTransaction.status)}
                      </Tag>
                    ) : (
                      "N/A"
                    )}
                  </span>
                }
                style={{ marginTop: "20px" }}
              >
                <Descriptions>
                  <Descriptions.Item label={<b>Product</b>} key="product">
                    {customerTransaction?.customerSubscription?.product?.name
                      ? `${customerTransaction.customer.petraId} - ${customerTransaction.customerSubscription.product.name}`
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Description</b>} key="description">
                    {customerTransaction?.description || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>External Id</b>} key="externalId">
                    {customerTransaction?.externalId || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={<b>Express Pay Platform Fee</b>}
                    key="platformFee"
                  >
                    {customerTransaction?.platformFee
                      ? formatter.format(customerTransaction.platformFee)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Amount</b>} key="totalAmount">
                    {customerTransaction?.totalAmount
                      ? formatter.format(customerTransaction.totalAmount)
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Type</b>} key="transactionType">
                    {customerTransaction?.transactionType
                      ? capitalizeFirstLetter(
                          formatSnakeCase(customerTransaction.transactionType),
                        )
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Payment Mode</b>} key="mode">
                    {customerTransaction?.mode
                      ? capitalizeFirstLetter(
                          formatSnakeCase(customerTransaction.mode),
                        )
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Provider</b>} key="provider">
                    {customerTransaction?.provider || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Source</b>} key="source">
                    {customerTransaction?.source
                      ? capitalizeFirstLetter(
                          formatSnakeCase(customerTransaction.source),
                        )
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Reason</b>} key="reason">
                    {reason?.reason?.reasonDescription || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Reason Comment</b>} key="reasonComment">
                    {reason?.comment || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Paid Amount</b>} key="paidAmount">
                    {reason?.amount || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Initial Redemption Amount</b>}
                    key="initialRedemptionAmount"
                  >
                    {customerTransaction?.initialRedemptionAmount
                      ? formatter.format(
                          customerTransaction.initialRedemptionAmount,
                        )
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={<b>TA Transaction Status</b>}
                    key="taTransactionStatus"
                  >
                    {customerTransaction?.taTransactionStatus ? (
                      <span
                        style={{
                          color:
                            customerTransaction.taTransactionStatus.toLowerCase() ===
                            "success"
                              ? "green"
                              : "red",
                        }}
                      >
                        {capitalizeFirstLetter(
                          customerTransaction.taTransactionStatus,
                        )}
                      </span>
                    ) : (
                      "N/A"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Status Reason</b>} key="statusReason">
                    {customerTransaction?.statusReason || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Engine</b>} key="engine">
                    {customerTransaction?.engine
                      ? capitalizeFirstLetter(customerTransaction.engine)
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={<b>Post Tax Amount</b>}
                    key="postTaxAmount"
                  >
                    {customerTransaction?.postTaxAmount
                      ? formatter.format(customerTransaction.postTaxAmount)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Pre Tax Amount</b>} key="preTaxAmount">
                    {customerTransaction?.preTaxAmount
                      ? formatter.format(customerTransaction.preTaxAmount)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Withholding Tax</b>}
                    key="withholdingTax"
                  >
                    {customerTransaction?.withholdingTax
                      ? formatter.format(customerTransaction.withholdingTax)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Withholding Tax Confirmed</b>}
                    key="withholdingTaxConfirmed"
                  >
                    {customerTransaction?.withholdingTaxConfirmed == null
                      ? "N/A"
                      : customerTransaction.withholdingTaxConfirmed
                      ? "Yes"
                      : "No"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Net Amount</b>} key="netAmount">
                    {customerTransaction?.netAmount
                      ? formatter.format(customerTransaction.netAmount)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Subcategory</b>} key="subCategory">
                    {customerTransaction?.subCategory?.name || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Lien Amount</b>} key="lienAmount">
                    {customerTransaction?.lienAmount
                      ? formatter.format(customerTransaction.lienAmount)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Vesting Percentage Override</b>}
                    key="vestingPercentage"
                  >
                    {customerTransaction?.vestingPercentage || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Redemption Request Purpose</b>}
                    key="redemptionRequestPurpose"
                  >
                    {customerTransaction?.redemptionRequestPurpose?.name ||
                      "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Redemption Request Sub Purpose</b>}
                    key="redemptionRequestSubPurpose"
                  >
                    {customerTransaction?.redemptionRequestSubPurpose || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Redemption Value</b>}
                    key="redemptionValue"
                  >
                    {customerTransaction?.redemptionValue
                      ? formatter.format(customerTransaction.redemptionValue)
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Redemption Type</b>}
                    key="redemptionType"
                  >
                    {customerTransaction?.redemptionType
                      ? capitalizeFirstLetter(
                          formatSnakeCase(customerTransaction.redemptionType),
                        )
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Redemption Preference</b>}
                    key="redemptionPreference"
                  >
                    {customerTransaction?.redemptionPreference || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Deal Synced?</b>}
                    key="dealProcessed"
                    style={{ textTransform: "capitalize" }}
                  >
                    {customerTransaction?.dealProcessed != null
                      ? customerTransaction.dealProcessed.toString()
                      : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Risk Percentage</b>}
                    key="riskPercentage"
                  >
                    {customerTransaction?.riskBreakdown ? (
                      <span style={{ color: "red" }}>
                        {customerTransaction.riskBreakdown["riskPercentage"]}%
                      </span>
                    ) : (
                      "N/A"
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </AntdCard>
              <AntdCard
                title="Dates and Timelines"
                style={{ marginTop: "20px" }}
              >
                <Descriptions>
                  <Descriptions.Item label={<b>Date</b>} key="createdAt">
                    {customerTransaction?.createdAt
                      ? moment(
                          customerTransaction.createdAt,
                          "YYYY-MM-DD HH:mm:ss",
                        ).format("llll")
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Paid Date</b>} key="paidDate">
                    {reason?.date ? moment(reason.date).format("llll") : "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label={<b>Price Date</b>} key="priceDate">
                    {customerTransaction?.priceDate
                      ? moment(
                          customerTransaction.priceDate,
                          "YYYY-MM-DD HH:mm:ss",
                        ).format("llll")
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={<b>Product Duration</b>}
                    key="productDuration"
                  >
                    {customerTransaction?.customerSubscription
                      ?.productDuration || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={<b>Correct Info Date</b>}
                    key="correctInfoDate"
                  >
                    {customerTransaction?.correctInfoDate
                      ? moment(
                          customerTransaction.correctInfoDate,
                          "YYYY-MM-DD HH:mm:ss",
                        ).format("llll")
                      : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </AntdCard>
            </Row>
            {availableTransitionStates && (
              <>
                <div
                  style={{
                    marginTop: "25px",
                    display: "flex",
                    justifyContent: "start",
                  }}
                >
                  {availableTransitionStates?.map((transition, i) => (
                    <Button
                      onClick={() => this.requestForReason(transition.state)}
                      className={this.generateButtonColor(transition.state)}
                      id={`transition-btn-${i}`}
                      key={i}
                      style={{ marginRight: "5px" }}
                      disabled={isReasonLoading}
                    >
                      {generateStateButtonText(transition.event)}
                    </Button>
                  ))}
                </div>

                {ModalCreator({
                  title: noReason ? "" : "Select Redemption Reason",
                  okType: "primary",
                  okText: noReason ? "Comment" : "Select Reason",
                  showModal: showReasonModal,
                  onOkay: this.saveStateReason,
                  toggleModal: this.toggleShowReasonModal,
                  isLoading: isReasonLoading,
                  okayButtonProps: {
                    disabled: isReasonLoading,
                  },
                  inscription: RequestReasonUI({
                    reference: this.reasonReference,
                    isLoading,
                    reasons,
                    selectedTransitionState,
                    handleReasonSelect: this.handleReasonSelect,
                    availableTransitionStates,
                    commentRequired,
                    type: "transaction",
                  }),
                })}
              </>
            )}
          </div>
        </Card>

        <Collapse style={{ marginTop: "50px", marginBottom: "50px" }}>
          <Panel
            header={
              <span>
                Redemption Allocation{" "}
                <Badge
                  count={customerTransaction?.allocationAmountBreakdown ? 1 : 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"RedemptionAllocation"}
          >
            {customerTransaction?.allocationAmountBreakdown && (
              <RedemptionAllocationBreakdown
                allocation={customerTransaction.allocationAmountBreakdown}
                redemptionRequest={customerTransaction}
              />
            )}
          </Panel>

          {customerTransaction?.deal && (
            <Panel
              header={
                <span>
                  Deal Information <Badge count={1} style={tabPaneStyle} />
                </span>
              }
              key="dealInfo"
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Descriptions
                    column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                    size="small"
                  >
                    <Descriptions.Item label="Deal Type">
                      {customerTransaction.deal.dealType}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag
                        color={
                          customerTransaction.deal.status === "Completed"
                            ? "green"
                            : "orange"
                        }
                      >
                        {customerTransaction.deal.status}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Description">
                      {customerTransaction.deal.dealDescription}
                    </Descriptions.Item>
                    <Descriptions.Item label="Transaction Type">
                      {customerTransaction.deal.transactionType}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Amount">
                      <Text strong>
                        {formatter.format(customerTransaction.deal.totalAmount || 0)}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Units">
                      <Text strong>
                        {customerTransaction.deal.totalUnits?.toFixed(4) || "0.0000"}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Price Date">
                      {customerTransaction.deal.priceDate
                        ? moment(customerTransaction.deal.priceDate).format("YYYY-MM-DD")
                        : "N/A"}
                    </Descriptions.Item>
                  </Descriptions>

                  {customerTransaction.deal.contributions &&
                    customerTransaction.deal.contributions.length > 0 && (
                      <>
                        <Divider orientation="left">
                          <Text strong>Deal Contributions</Text>
                        </Divider>
                        <Table
                          dataSource={customerTransaction.deal.contributions
                            .map((contribution: any) => {
                              const amount = contribution.amount || 0;
                              const units = contribution.units || 0;
                              return {
                                ...contribution,
                                type: spaceCamel(contribution.deal_contribution_type || ""),
                                amount,
                                units,
                              };
                            })
                            .filter((c: any) => c.amount > 0 || c.units > 0)}
                          pagination={false}
                          size="small"
                          rowKey={(record: any) => record.id || record.type}
                        >
                          <Table.Column
                            title="Type"
                            dataIndex="type"
                            key="type"
                            render={(text) => (
                              <Text strong>{formatSnakeCase(text)}</Text>
                            )}
                          />
                          <Table.Column
                            title="Amount (GHS)"
                            dataIndex="amount"
                            key="amount"
                            align="right"
                            render={(value) => formatter.format(value || 0)}
                          />
                          <Table.Column
                            title="Units"
                            dataIndex="units"
                            key="units"
                            align="right"
                            render={(value) => value?.toFixed(4) || "0.0000"}
                          />
                        </Table>
                      </>
                    )}
                </Col>
              </Row>
            </Panel>
          )}

          <Panel
            header={
              <span>
                Customer Transaction Payout Account{" "}
                <Badge
                  count={customerTransaction?.payoutAccount ? 1 : 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"CustomerTransactionPayoutAccount"}
          >
            {customerTransaction?.payoutAccount && (
              <Col span={8} style={{ marginTop: "20px" }}>
                <AntdCard
                  title={<b>{"Payout Account"}</b>}
                  size="small"
                  style={{
                    width: 300,
                    borderTop: "4px solid rgb(29, 55, 100)",
                    borderRadius: "4px",
                  }}
                >
                  <PaymentAccountCard
                    paymentAccount={customerTransaction?.payoutAccount}
                  />
                </AntdCard>
              </Col>
            )}
          </Panel>

          <Panel
            header="Customer Lien Information"
            key={"customerLienInformation"}
          >
            {allCustomerLiens && allCustomerLiens.length > 0 && (
              <h3>Customer Lien Information</h3>
            )}
            {allCustomerLiens &&
              allCustomerLiens?.map((lien: CustomerLien, index: number) => {
                return (
                  <>
                    <Descriptions layout={"horizontal"} key={index}>
                      <Descriptions.Item label={<b>Employee Pre tax</b>}>
                        {formatter.format(lien.employeePreTax)}
                      </Descriptions.Item>
                      <Descriptions.Item label={<b>Employee Post tax</b>}>
                        {formatter.format(lien.employeePostTax)}
                      </Descriptions.Item>
                      <Descriptions.Item label={<b>Employer Pre tax</b>}>
                        {formatter.format(lien.employerPreTax)}
                      </Descriptions.Item>
                      <Descriptions.Item label={<b>Employer Post tax</b>}>
                        {formatter.format(lien.employerPostTax)}
                      </Descriptions.Item>
                      <Descriptions.Item label={<b>Provider</b>}>
                        {capitalizeFirstLetter(
                          formatSnakeCase(lien?.provider || ""),
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={<b>{formatSnakeCase("Total Amount")}</b>}
                      >
                        {formatter.format(lien?.totalAmount)}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={<b>{formatSnakeCase("Current Amount")}</b>}
                      >
                        {formatter.format(lien?.currentAmount)}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                );
              })}
          </Panel>

          <Panel
            header={
              <span>
                Risk Breakdown{" "}
                <Badge
                  count={
                    customerTransaction?.riskBreakdown?.riskCounts?.length || 0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key="RiskBreakdown"
          >
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              {customerTransaction?.riskBreakdown?.riskCounts?.map(
                (riskGroup: any) => {
                  const groupedRisks =
                    customerTransaction?.riskBreakdown.risks.filter(
                      (risk: any) => risk.riskName === riskGroup.riskName,
                    );

                  return (
                    <Col
                      key={riskGroup.riskName}
                      xs={24}
                      sm={24}
                      md={12}
                      lg={8}
                      xl={8}
                    >
                      <AntdCard
                        title={
                          <div>
                            <b>{riskGroup.riskName}</b>{" "}
                            <Badge
                              count={riskGroup.count}
                              style={tabPaneStyle}
                            />
                          </div>
                        }
                        size="small"
                        style={{
                          borderTop: "4px solid rgb(29, 55, 100)",
                          borderRadius: "4px",
                          width: "100%",
                        }}
                      >
                        {groupedRisks.map((risk: any) => (
                          <div
                            key={risk.id}
                            style={{
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "14px",
                            }}
                          >
                            <div>
                              <b>Customer:</b> {risk.customer}
                            </div>
                            {risk.paymentId && risk.paymentId !== "0" ? (
                              <div>
                                <b>Payment ID:</b> {risk.paymentId}
                              </div>
                            ) : (
                              risk.redemptionExternalReference !==
                                "00000000-0000-0000-0000-000000000000" && (
                                <div>
                                  <b>Redemption External Reference:</b>{" "}
                                  {risk.redemptionExternalReference}
                                </div>
                              )
                            )}
                            <div>
                              <b>Created At:</b>{" "}
                              {moment(risk?.createdAt, "YYYY-MM-DD").format(
                                "llll",
                              )}
                            </div>
                            <div>
                              <b>Created By:</b> {risk.createdBy}
                            </div>
                            <div>
                              <b>Risk Point:</b> {risk.riskPoint}
                            </div>
                          </div>
                        ))}
                      </AntdCard>
                    </Col>
                  );
                },
              )}
            </Row>
          </Panel>

          <Panel
            header={
              <span>
                Attachments{" "}
                <Badge
                  count={
                    customerTransaction?.redemptionRequestAttachments?.length ||
                    0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key="Attachments"
          >
            <AttachmentsSection
              reference={this.attachmentReference}
              createAttachment={this.createAttachment}
              attachments={attachments}
              showForm={true}
              handleRemove={this.unassignAttachmentFromCustomerTransaction}
            />
          </Panel>

          <Panel
            header={
              <span>
                Notes{" "}
                <Badge
                  count={
                    customerTransaction?.customerTransactionNotes?.length || 0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key="Notes"
          >
            <NotesSection
              showHeading={true}
              reference={this.noteReference}
              createNote={this.createNote}
              notes={notes}
              showForm={true}
              entity="CustomerTransaction"
              onNoteUpdate={this.handleNoteUpdate}
            />
          </Panel>
        </Collapse>
        <Drawer
          title={this.getTitleBasedOnDrawerContent()}
          width={1200}
          onClose={this.toggleModal}
          visible={showDrawer}
          bodyStyle={{ paddingBottom: 80 }}
          maskClosable={false}
        >
          {this.getDrawerContent()}
        </Drawer>
        {this.state.showCancelModal &&
          ModalCreator({
            title: "Cancel",
            okType: "primary",
            okText: "Yes",
            width: 400,
            showModal: true,
            onOkay: this.toggleDrawer(""),
            toggleModal: this.toggleModal,
            showFooter: true,
            inscription: `Are you sure you want to cancel ${spaceCamel(
              this.state.activeDrawerContent,
            ).toLocaleLowerCase()} ?`,
          })}
        {showConfirmationModal &&
          ModalCreator({
            showModal: showConfirmationModal,
            onOkay: this.updateCustomerTransactionAllocationAmount,
            toggleModal: this.toggleConfirmationModal,
            inscription: "Are you sure you want to complete this action?",
          })}
      </Spin>
    );
  }
}
